import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from "react";

/**
 * SnapshotPanel — inline "freeze / restore" controls for the play-through editor.
 *
 * A snapshot freezes the current *viewable* version of the play-through — the
 * bundle behind the last successful finalize — so a later re-author (which
 * overwrites the working bundle) can't destroy a good version. When nothing has
 * been finalized yet, it falls back to freezing the current authored draft.
 *
 * The panel owns all snapshot state and API calls. The parent uses it two ways:
 *   1. Rendered inline for manual Save-snapshot / Restore / Delete.
 *   2. Via ref.captureSnapshot() — called from the parent's re-author handler as
 *      a backstop offer to freeze the current version before it's overwritten.
 *
 * Props:
 *   clientId       — client UUID (string)
 *   status         — latest playthrough status object (for can_snapshot / source)
 *   onRestored     — callback() after a successful restore, so the parent can
 *                    reload the bundle from the server.
 *   disabled       — true while generate/finalize is in flight.
 */
const SnapshotPanel = forwardRef(function SnapshotPanel(
  { clientId, base: baseProp, status, onRestored, onCountChange, disabled },
  ref
) {
  const [snapshots, setSnapshots] = useState([]);
  const [loading,   setLoading]   = useState(false);
  const [busy,      setBusy]      = useState(false);
  const [error,     setError]     = useState(null);
  const [open,      setOpen]      = useState(false);
  const [editingId,    setEditingId]    = useState(null);
  const [editingLabel, setEditingLabel] = useState("");

  const canSnapshot   = Boolean(status?.can_snapshot);
  const sourceIsDraft = status?.snapshot_source === "draft";

  // A section play-through passes its own base (Oct 20); the intro's is the default.
  const base = baseProp || `/api/clients/${clientId}/playthrough/snapshots`;

  // Keep the latest onCountChange in a ref so load() doesn't depend on it
  // (parent passes an inline callback; depending on it would churn load's
  // identity every render and cause a refetch loop).
  const onCountChangeRef = React.useRef(onCountChange);
  useEffect(() => { onCountChangeRef.current = onCountChange; }, [onCountChange]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(base);
      const data = await res.json();
      if (res.ok) {
        const list = data.snapshots || [];
        setSnapshots(list);
        if (onCountChangeRef.current) onCountChangeRef.current(list.length);
      }
    } catch {
      /* non-fatal: panel simply shows no list */
    } finally {
      setLoading(false);
    }
  }, [base]);

  useEffect(() => { load(); }, [load]);

  // Reload the list whenever the snapshot count changes underneath us
  // (e.g. a capture triggered by the parent's re-author flow).
  useEffect(() => {
    if (typeof status?.snapshot_count === "number" &&
        status.snapshot_count !== snapshots.length) {
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.snapshot_count]);

  /**
   * Capture a snapshot of the current viewable version.
   * Returns the created snapshot metadata, or null on failure.
   * Exposed to the parent via ref for the re-author backstop offer.
   */
  const captureSnapshot = useCallback(async (label) => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(base, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(label ? { label } : {}),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Could not save snapshot"); return null; }
      await load();
      return data.snapshot || null;
    } catch (e) {
      setError("Could not save snapshot: " + e.message);
      return null;
    } finally {
      setBusy(false);
    }
  }, [base, load]);

  useImperativeHandle(ref, () => ({ captureSnapshot }), [captureSnapshot]);

  async function handleSave() {
    const label = window.prompt(
      "Name this snapshot (optional) — e.g. \"before re-authoring scene 2\":",
      ""
    );
    // prompt returns null on Cancel → abort; empty string is allowed (no label)
    if (label === null) return;
    await captureSnapshot(label.trim() || undefined);
  }

  async function handleRestore(snap) {
    const when = formatWhen(snap.created_at);
    const name = snap.label ? `"${snap.label}" (${when})` : when;
    const ok = window.confirm(
      `Restore snapshot ${name}?\n\n` +
      "This replaces the current stream content with the snapshot. " +
      "Any edits or re-authoring done since this snapshot will be discarded. " +
      "You'll need to Re-finalize to make the restored version live."
    );
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${base}/${snap.id}/restore`, { method: "POST" });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Restore failed"); return; }
      await load();
      if (onRestored) onRestored();
    } catch (e) {
      setError("Restore failed: " + e.message);
    } finally {
      setBusy(false);
    }
  }

  async function handleDelete(snap) {
    const ok = window.confirm("Delete this snapshot? This can't be undone.");
    if (!ok) return;
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${base}/${snap.id}`, { method: "DELETE" });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Delete failed"); return; }
      await load();
    } catch (e) {
      setError("Delete failed: " + e.message);
    } finally {
      setBusy(false);
    }
  }

  function startRename(snap) {
    setEditingId(snap.id);
    setEditingLabel(snap.label || "");
  }

  function cancelRename() {
    setEditingId(null);
    setEditingLabel("");
  }

  async function commitRename(snap) {
    // Guard against blur firing after Escape/cancel already closed the editor.
    if (editingId !== snap.id) return;
    const next = editingLabel.trim();
    // No change → just close the editor.
    if (next === (snap.label || "")) { cancelRename(); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(`${base}/${snap.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ label: next }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || "Rename failed"); return; }
      cancelRename();
      await load();
    } catch (e) {
      setError("Rename failed: " + e.message);
    } finally {
      setBusy(false);
    }
  }

  const count = snapshots.length;

  return (
    <div style={st.panel}>
      <button
        style={st.headerBtn}
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span style={st.headerTitle}>
          Snapshots{count > 0 ? ` (${count})` : ""}
        </span>
        <span style={st.chevron}>{open ? "▾" : "▸"}</span>
      </button>

      {open && (
        <div style={st.body}>
          <p style={st.blurb}>
            A snapshot freezes the current version so you can return to it later.
            {sourceIsDraft
              ? " Nothing is finalized yet, so this saves the current draft."
              : " This saves the version behind the live stream (unsaved edits aren't included)."}
          </p>

          <div style={st.actionRow}>
            <button
              style={{ ...st.saveBtn, ...((!canSnapshot || busy || disabled) ? st.muted : {}) }}
              onClick={handleSave}
              disabled={!canSnapshot || busy || disabled}
              title={canSnapshot ? "Save a snapshot of the current version" : "Nothing to snapshot yet"}
            >{busy ? "Working…" : "Save snapshot"}</button>
          </div>

          {error && <p style={st.error}>{error}</p>}

          {loading ? (
            <p style={st.empty}>Loading…</p>
          ) : count === 0 ? (
            <p style={st.empty}>No snapshots yet.</p>
          ) : (
            <ul style={st.list}>
              {snapshots.map(snap => (
                <li key={snap.id} style={st.item}>
                  <div style={st.itemMain}>
                    {editingId === snap.id ? (
                      <input
                        style={st.renameInput}
                        value={editingLabel}
                        onChange={e => setEditingLabel(e.target.value)}
                        onKeyDown={e => {
                          if (e.key === "Enter") commitRename(snap);
                          if (e.key === "Escape") cancelRename();
                        }}
                        onBlur={() => commitRename(snap)}
                        placeholder="Name this snapshot"
                        autoFocus
                        maxLength={120}
                      />
                    ) : (
                      <span
                        style={{ ...st.itemLabel, ...(snap.label ? {} : st.itemLabelUnnamed) }}
                        onClick={() => !busy && !disabled && startRename(snap)}
                        title="Click to rename"
                      >
                        {snap.label || formatWhen(snap.created_at)}
                        <button
                          style={st.renamePencil}
                          onClick={e => { e.stopPropagation(); startRename(snap); }}
                          disabled={busy || disabled}
                          title="Rename"
                          aria-label="Rename snapshot"
                        >✎</button>
                      </span>
                    )}
                    <span style={st.itemMeta}>
                      {snap.label ? `${formatWhen(snap.created_at)} · ` : ""}
                      {snap.source === "finalized" ? "live version" : "draft"}
                      {typeof snap.scene_count === "number" ? ` · ${snap.scene_count} scenes` : ""}
                    </span>
                  </div>
                  <div style={st.itemActions}>
                    <button
                      style={{ ...st.restoreBtn, ...((busy || disabled) ? st.muted : {}) }}
                      onClick={() => handleRestore(snap)}
                      disabled={busy || disabled}
                    >Restore</button>
                    <button
                      style={{ ...st.deleteBtn, ...((busy || disabled) ? st.muted : {}) }}
                      onClick={() => handleDelete(snap)}
                      disabled={busy || disabled}
                      title="Delete this snapshot"
                    >Delete</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
});

function formatWhen(iso) {
  if (!iso) return "";
  try {
    // stored as naive UTC isoformat; append Z so it's parsed as UTC
    const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
    return d.toLocaleString(undefined, {
      month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

const st = {
  panel: {
    border: "1px solid var(--color-bg-inset)",
    borderRadius: "var(--radius-md)",
    background: "var(--color-bg-base)",
  },
  headerBtn: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "var(--space-3) var(--space-4)",
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-primary)",
  },
  headerTitle: { fontSize: "var(--text-sm)", fontWeight: "var(--weight-semibold)" },
  chevron:     { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  body: {
    padding: "0 var(--space-4) var(--space-4)",
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  },
  blurb: {
    fontSize: "var(--text-xs)",
    color: "var(--color-text-secondary)",
    lineHeight: "var(--leading-normal)",
    margin: 0,
  },
  actionRow: { display: "flex", gap: "var(--space-2)" },
  saveBtn: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-text-on-accent)",
    background: "var(--color-accent)",
    border: "none",
    borderRadius: "var(--radius-sm)",
    padding: "6px var(--space-4)",
    cursor: "pointer",
  },
  muted: { opacity: 0.5, cursor: "not-allowed" },
  error: {
    fontSize: "var(--text-xs)",
    color: "var(--color-critical)",
    margin: 0,
  },
  empty: {
    fontSize: "var(--text-xs)",
    color: "var(--color-text-tertiary)",
    margin: 0,
  },
  list: {
    listStyle: "none",
    margin: 0,
    padding: 0,
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-2)",
  },
  item: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "var(--space-3)",
    padding: "var(--space-2) var(--space-3)",
    background: "var(--color-bg-subtle)",
    borderRadius: "var(--radius-sm)",
  },
  itemMain: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  itemLabel: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-primary)",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
  },
  itemLabelUnnamed: { color: "var(--color-text-secondary)", fontStyle: "italic" },
  renamePencil: {
    fontSize: "var(--text-xs)",
    color: "var(--color-text-tertiary)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
    flexShrink: 0,
  },
  renameInput: {
    fontSize: "var(--text-sm)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-primary)",
    background: "var(--color-bg-base)",
    border: "1px solid var(--color-accent)",
    borderRadius: "var(--radius-sm)",
    padding: "2px 6px",
    width: "100%",
    boxSizing: "border-box",
    fontFamily: "inherit",
  },
  itemMeta: { fontSize: "var(--text-xs)", color: "var(--color-text-tertiary)" },
  itemActions: { display: "flex", gap: "var(--space-2)", flexShrink: 0 },
  restoreBtn: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-semibold)",
    color: "var(--color-accent)",
    background: "none",
    border: "1px solid var(--color-accent)",
    borderRadius: "var(--radius-sm)",
    padding: "2px 10px",
    cursor: "pointer",
  },
  deleteBtn: {
    fontSize: "var(--text-xs)",
    fontWeight: "var(--weight-medium)",
    color: "var(--color-text-tertiary)",
    background: "none",
    border: "1px solid var(--color-bg-inset)",
    borderRadius: "var(--radius-sm)",
    padding: "2px 10px",
    cursor: "pointer",
  },
};

export default SnapshotPanel;
